<template>
    <div class="platform-detail-page" v-loading="loading || searchLoading">
        <div v-if="pageMode === 'detail'">
            <el-card shadow="never" class="info-card">
                <div class="card-header">
                    <div class="platform-title-row">
                        <div class="platform-logo" :class="platformClass">
                            <svg-icon v-if="platformType === 'qq'" icon-class="new-qq"></svg-icon>
                            <svg-icon v-else icon-class="new-wyy"></svg-icon>
                        </div>
                        <span class="platform-name-text">{{ platformName }}艺人</span>
                    </div>
                    <div class="badge" :class="`badge-${statusBadgeType}`">{{ statusBadgeText }}</div>
                </div>
                <div class="info-rows">
                    <div class="info-row-two-col">
                        <div class="info-item">
                            <span class="info-label">平台艺人 ID</span>
                            <span class="info-value">
                                {{ platformArtistId || '-' }}
                                <el-button v-if="platformArtistId" type="text" class="copy-btn" @click="copyId">复制</el-button>
                            </span>
                        </div>
                        <div class="info-item">
                            <span class="info-label">平台主页</span>
                            <span v-if="platformArtistId" class="info-value link" @click="openHomePage">打开{{ platformName }}主页 ↗</span>
                            <span v-else class="info-value">-</span>
                        </div>
                    </div>
                    <div class="info-row-two-col">
                        <div class="info-item">
                            <span class="info-label">关联时间</span>
                            <span class="info-value">{{ linkedAt || '-' }}</span>
                        </div>
                        <div class="info-item">
                            <span class="info-label">最近同步时间</span>
                            <span class="info-value">{{ syncedAt || '-' }}</span>
                        </div>
                    </div>
                </div>

                <div class="platform-info-section">
                    <div class="section-title">平台信息</div>
                    <div v-if="hasPlatformInfo" class="profile-content">
                        <div class="avatar-wrapper">
                            <el-image
                                v-if="platformInfo.avatar"
                                :src="platformInfo.avatar"
                                fit="cover"
                                class="avatar profile-avatar"
                                :preview-src-list="[platformInfo.avatar]"
                                :z-index="4000"
                            >
                                <div slot="error" class="avatar-placeholder profile-placeholder">
                                    <i class="el-icon-picture-outline"></i>
                                    <span class="placeholder-text">暂无图片</span>
                                </div>
                            </el-image>
                            <div v-else class="avatar-placeholder profile-placeholder">
                                <i class="el-icon-picture-outline"></i>
                                <span class="placeholder-text">暂无图片</span>
                            </div>
                        </div>
                        <div class="profile-info">
                            <div class="artist-name">{{ platformInfo.name || '-' }}</div>
                            <div
                                v-if="platformType === 'qq' && platformInfo.fansCount !== undefined && platformInfo.fansCount > 0"
                                class="fans-count"
                            >
                                <strong>{{ formatNumber(platformInfo.fansCount) }}</strong>
                                <span>粉丝数</span>
                            </div>
                            <div v-if="platformInfo.intro" class="artist-desc">{{ platformInfo.intro }}</div>
                            <div v-if="platformInfo.representative && platformInfo.representative.length" class="tags-line">
                                <span class="tags-label">代表作：</span>
                                <span v-for="(tag, idx) in platformInfo.representative" :key="idx" class="info-tag">
                                    <el-tag>{{ tag }}</el-tag>
                                </span>
                            </div>
                            <div v-if="platformType === 'qq' && platformInfo.members && platformInfo.members.length" class="tags-line m-top_16">
                                <span class="tags-label">乐队成员：</span>
                                <span v-for="(member, idx) in platformInfo.members" :key="idx" class="info-tag">
                                    <el-tag>{{ member }}</el-tag>
                                </span>
                            </div>
                        </div>
                    </div>
                    <div v-else-if="isStatusBound" class="unsync-list">
                        <div class="unsync-row">
                            <span class="unsync-label">艺人头像</span>
                            <span class="unsync-value">未同步</span>
                        </div>
                        <div class="unsync-row">
                            <span class="unsync-label">艺人名称</span>
                            <span class="unsync-value">未同步</span>
                        </div>
                        <div class="unsync-row">
                            <span class="unsync-label">代表作</span>
                            <span class="unsync-value">未同步</span>
                        </div>
                        <div class="unsync-row">
                            <span class="unsync-label">简介</span>
                            <span class="unsync-value">未同步</span>
                        </div>
                        <div v-if="platformType === 'qq'" class="unsync-row">
                            <span class="unsync-label">粉丝数量</span>
                            <span class="unsync-value">未同步</span>
                        </div>
                        <div v-if="platformType === 'qq'" class="unsync-row">
                            <span class="unsync-label">乐队成员</span>
                            <span class="unsync-value">未同步</span>
                        </div>
                    </div>
                    <div v-else class="empty-hint">
                        暂无关联信息
                    </div>
                </div>
            </el-card>

            <div class="actions-bar">
                <el-button @click="goBack">返回</el-button>
                <template v-if="isStatusBound">
                    <el-button type="danger" plain @click="handleUnbind">解除关联</el-button>
                    <el-button
                        v-if="!hideSyncButton"
                        plain
                        class="sync-btn"
                        :class="{'is-disabled-btn': disableSyncButton}"
                        :disabled="disableSyncButton"
                        @click="handleSync"
                        :loading="syncLoading"
                    >
                        同步平台信息
                    </el-button>
                    <el-button type="primary" @click="showRelinkConfirm">重新关联</el-button>
                </template>
                <template v-else>
                    <el-button type="primary" @click="goToBind">去关联</el-button>
                </template>
            </div>
        </div>

        <div v-else>
            <el-card shadow="never" class="bind-card">
                <div class="tab-switch">
                    <div class="tab-btn" :class="{active: searchTab === 'name'}" @click="switchTab('name')">名称搜索关联</div>
                    <div class="tab-btn" :class="{active: searchTab === 'id'}" @click="switchTab('id')">ID直接关联</div>
                    <div class="tab-btn" :class="{active: searchTab === 'create'}" @click="switchTab('create')">新建主页</div>
                </div>

                <div v-if="searchTab === 'create'" class="create-main-page">
                    <div class="create-title">让{{ platformName }}新创建主页</div>
                    <div class="create-tip">
                        注意：专辑上线后，请及时关联新创建的主页id，<br />
                        否则随后的新专辑/歌曲上线时，{{ platformName }}可能会再次新建不同主页
                    </div>
                </div>

                <div v-else-if="searchTab === 'name'">
                    <div class="search-row">
                        <el-input
                            v-model="searchKeyword"
                            placeholder="请输入艺人名称"
                            class="search-input"
                            clearable
                            @keyup.enter.native="handleSearch"
                        ></el-input>
                        <el-button type="primary" class="search-btn" @click="handleSearch">搜索</el-button>
                    </div>

                    <div v-if="searchError" class="error-warn">
                        <div class="error-text">
                            <i class="el-icon-warning-outline"></i>
                            {{ searchError }}
                        </div>
                        <div class="error-actions">
                            <el-button size="small" @click="handleSearch">重试</el-button>
                            <el-button size="small" type="primary" @click="switchToManual">手动输入 ID 关联</el-button>
                        </div>
                    </div>

                    <div v-else-if="manualMode" class="manual-area m-top_24">
                        <div class="search-row">
                            <el-input v-model="manualId" :placeholder="`请输入${platformName}艺人ID`" class="search-input" clearable></el-input>
                        </div>
                        <div class="manual-tip m-top_16">
                            <span class="tip-label">平台主页：</span>
                            <span class="tip-link" @click="openManualHomePage">{{ getManualHomeUrl() }}</span>
                        </div>
                        <el-alert
                            title="当前无法校验该 ID，也无法获取艺人名称、头像等信息。请确认 ID 正确后再保存。"
                            type="warning"
                            :show-icon="true"
                            :closable="false"
                            class="m-top_16"
                        ></el-alert>
                    </div>

                    <div v-else-if="searchResults.length" class="results-list">
                        <div
                            v-for="(item, idx) in searchResults"
                            :key="idx"
                            class="result-item"
                            :class="{active: selectedResult === idx}"
                            @click="selectedResult = idx"
                        >
                            <div class="result-left">
                                <div class="custom-radio" :class="{checked: selectedResult === idx}">
                                    <div v-if="selectedResult === idx" class="radio-dot"></div>
                                </div>
                                <div class="result-avatar">
                                    <el-image v-if="item.avatar" :src="item.avatar" fit="cover">
                                        <div slot="error" class="avatar-placeholder-small">
                                            <i class="el-icon-picture-outline"></i>
                                        </div>
                                    </el-image>
                                    <div v-else class="avatar-placeholder-small">
                                        <i class="el-icon-picture-outline"></i>
                                    </div>
                                </div>
                                <div class="result-info">
                                    <div class="result-name">{{ item.name }}</div>
                                    <div class="result-sub">ID：{{ item.id }}</div>
                                    <div class="result-sub">
                                        代表作：{{ item.representative && item.representative.length ? item.representative.join('、') : '-' }}
                                    </div>
                                </div>
                            </div>
                            <el-button v-if="item.url" type="text" class="open-home-btn" @click.stop="openResultHome(item)">打开主页</el-button>
                        </div>
                    </div>

                    <div v-if="searchResults.length && totalPage >= 1" class="pagination-wrapper">
                        <el-pagination
                            background
                            layout="total, sizes, prev, pager, next, jumper"
                            :current-page="currentPage"
                            :page-size="pageSize"
                            :page-sizes="[10, 20, 50, 100]"
                            :total="totalCount"
                            @current-change="handlePageChange"
                            @size-change="handleSizeChange"
                        ></el-pagination>
                    </div>
                </div>

                <div v-else>
                    <div class="search-row">
                        <el-input
                            v-model="searchId"
                            :placeholder="`请输入${platformName}艺人ID`"
                            class="search-input"
                            clearable
                            @keyup.enter.native="handleSearchById"
                        ></el-input>
                        <el-button type="primary" class="search-btn" @click="handleSearchById">查询</el-button>
                    </div>

                    <div v-if="idSearchError" class="error-warn m-top_16">
                        <div class="error-text">
                            <i class="el-icon-warning-outline"></i>
                            {{ idSearchError }}
                        </div>
                        <div class="error-actions">
                            <el-button size="small" @click="handleSearchById">重试</el-button>
                            <el-button size="small" type="primary" @click="switchToManual">手动输入 ID 关联</el-button>
                        </div>
                    </div>

                    <div v-else-if="idSearchResult" class="id-result m-top_24">
                        <div class="id-profile-content">
                            <div class="id-avatar-wrapper">
                                <el-image v-if="idSearchResult.avatar" :src="idSearchResult.avatar" fit="cover" class="id-avatar">
                                    <div slot="error" class="id-avatar-placeholder">
                                        <i class="el-icon-picture-outline"></i>
                                        <span class="placeholder-text">暂无图片</span>
                                    </div>
                                </el-image>
                                <div v-else class="id-avatar-placeholder">
                                    <i class="el-icon-picture-outline"></i>
                                    <span class="placeholder-text">暂无图片</span>
                                </div>
                            </div>
                            <div class="id-profile-info">
                                <div class="id-artist-name">{{ idSearchResult.name || '-' }}</div>
                                <div
                                    v-if="platformType === 'qq' && idSearchResult.fansCount !== undefined && idSearchResult.fansCount > 0"
                                    class="id-fans-count"
                                >
                                    <strong>{{ formatNumber(idSearchResult.fansCount) }}</strong>
                                    <span>粉丝数</span>
                                </div>
                                <div v-if="idSearchResult.intro" class="id-artist-desc">{{ idSearchResult.intro }}</div>
                                <div v-if="idSearchResult.representative && idSearchResult.representative.length" class="id-tags-line">
                                    <span class="id-tags-label">代表作：</span>
                                    <span v-for="(tag, idx) in idSearchResult.representative" :key="idx" class="id-tag-item">
                                        <el-tag>{{ tag }}</el-tag>
                                    </span>
                                </div>
                                <div
                                    v-if="platformType === 'qq' && idSearchResult.members && idSearchResult.members.length"
                                    class="id-tags-line m-top_16"
                                >
                                    <span class="id-tags-label">乐队成员：</span>
                                    <span v-for="(member, idx) in idSearchResult.members" :key="idx" class="id-tag-item">
                                        <el-tag>{{ member }}</el-tag>
                                    </span>
                                </div>
                            </div>
                        </div>
                    </div>

                    <div v-else-if="manualMode" class="manual-area m-top_24">
                        <div class="search-row">
                            <el-input v-model="manualId" :placeholder="`请输入${platformName}艺人ID`" class="search-input" clearable></el-input>
                        </div>
                        <div class="manual-tip m-top_16">
                            <span class="tip-label">平台主页：</span>
                            <span class="tip-link" @click="openManualHomePage">{{ getManualHomeUrl() }}</span>
                        </div>
                        <el-alert
                            title="当前无法校验该 ID，也无法获取艺人名称、头像等信息。请确认 ID 正确后再保存。"
                            type="warning"
                            :show-icon="true"
                            :closable="false"
                            class="m-top_16"
                        ></el-alert>
                    </div>
                </div>
            </el-card>

            <div class="actions-bar">
                <el-button @click="cancelBind">取消</el-button>
                <el-button v-if="searchTab === 'create'" type="primary" :loading="submitLoading" @click="confirmCreate">确认创建</el-button>
                <el-button v-else type="primary" :loading="submitLoading" @click="confirmBind">确认关联</el-button>
            </div>
        </div>

        <el-dialog title="重新关联" :visible.sync="relinkDialogVisible" width="500px" :append-to-body="true" class="relink-dialog">
            <div class="dialog-content">重新关联将替换当前{{ platformName }}艺人 ID，是否继续？</div>
            <div slot="footer">
                <el-button @click="relinkDialogVisible = false">取消</el-button>
                <el-button type="primary" @click="goToBind">继续</el-button>
            </div>
        </el-dialog>

        <el-dialog title="确认替换关联" :visible.sync="replaceDialogVisible" width="500px" :append-to-body="true">
            <div class="dialog-content">
                确认将{{ platformName }} ID 从
                <br />
                <strong>{{ platformArtistId }}</strong>
                <br />
                替换为
                <br />
                <strong>{{ getNewId() }}</strong>
                吗？
            </div>
            <div slot="footer">
                <el-button @click="replaceDialogVisible = false">取消</el-button>
                <el-button type="primary" @click="doReplace">确认替换</el-button>
            </div>
        </el-dialog>

        <el-dialog title="确认手动关联" :visible.sync="manualDialogVisible" width="500px" :append-to-body="true">
            <div class="dialog-content">
                当前无法校验该 ID。
                <br />
                确认关联{{ platformName }} ID：
                <strong>{{ manualId }}</strong>
                吗？
            </div>
            <div slot="footer">
                <el-button @click="manualDialogVisible = false">取消</el-button>
                <el-button type="primary" @click="doManualBind">确认关联</el-button>
            </div>
        </el-dialog>

        <el-dialog title="确认关联" :visible.sync="linkConfirmDialogVisible" width="500px" :append-to-body="true">
            <div class="dialog-content">是否确认关联{{ platformName }}？</div>
            <div slot="footer">
                <el-button @click="linkConfirmDialogVisible = false">取消</el-button>
                <el-button type="primary" @click="confirmLink">确认</el-button>
            </div>
        </el-dialog>
    </div>
</template>

<script>
import {relatePlatformArtist, syncPlatformArtist, unrelatePlatformArtist, searchPlatformArtist, getPlatformArtistBindingDetail, markCreatePlatformArtist} from '@/api/album';

const PLATFORM_TYPE_MAP = {
    netease: 1,
    qq: 2
};

export default {
    props: {
        artistId: {
            type: [Number, String],
            required: true
        },
        platformType: {
            type: String,
            required: true,
            validator: val => ['netease', 'qq'].includes(val)
        },
        isDrawer: {
            type: Boolean,
            default: false
        },
        defaultBindMode: {
            type: Boolean,
            default: false
        },
        artistName: {
            type: String,
            default: ''
        },
        hideSyncButton: {
            type: Boolean,
            default: false
        },
        disableSyncButton: {
            type: Boolean,
            default: false
        }
    },
    data() {
        return {
            loading: false,
            pageMode: 'detail',
            isBound: false,
            bindStatus: 0,
            platformArtistId: '',
            platformUrl: '',
            linkedAt: '',
            syncedAt: '',
            hasPlatformInfo: false,
            platformInfo: {
                avatar: '',
                name: '',
                fansCount: 0,
                intro: '',
                representative: [],
                members: []
            },
            syncLoading: false,
            searchTab: 'name',
            searchKeyword: '',
            searchId: '',
            searchLoading: false,
            hasSearchedName: false,
            hasSearchedId: false,
            searchResults: [],
            selectedResult: -1,
            currentPage: 1,
            pageSize: 10,
            totalCount: 0,
            totalPage: 0,
            idSearchResult: null,
            idSearchError: '',
            manualMode: false,
            manualId: '',
            searchError: '',
            submitLoading: false,
            relinkDialogVisible: false,
            replaceDialogVisible: false,
            manualDialogVisible: false,
            linkConfirmDialogVisible: false
        };
    },
    computed: {
        platformName() {
            return this.platformType === 'netease' ? '网易云音乐' : 'QQ音乐';
        },
        platformClass() {
            return this.platformType === 'netease' ? 'netease-logo' : 'qq-logo';
        },
        platformTypeCode() {
            return PLATFORM_TYPE_MAP[this.platformType];
        },
        statusBadgeText() {
            if (this.bindStatus === 1) return '已关联';
            if (this.bindStatus === 2) return '新建主页';
            return '未关联';
        },
        statusBadgeType() {
            if (this.bindStatus === 1) return 'bound';
            if (this.bindStatus === 2) return 'special';
            return 'unbound';
        },
        isStatusBound() {
            return this.bindStatus === 1;
        },
        pageTitle() {
            return this.pageMode === 'detail' ? `${this.platformName}关联详情` : `关联 ${this.platformName}艺人`;
        }
    },
    created() {
        this.fetchBindingDetail();
    },
    methods: {
        extractNames(arr) {
            if (!arr) return [];
            if (typeof arr === 'string') {
                return arr
                    .split(/[、,]/)
                    .map(item => item.trim())
                    .filter(item => item);
            }
            if (!Array.isArray(arr)) return [];
            return arr
                .map(item => {
                    if (typeof item === 'object' && item !== null && item.name) {
                        return item.name;
                    }
                    return item;
                })
                .filter(item => typeof item === 'string' && item.trim());
        },
        async fetchBindingDetail() {
            this.loading = true;
            try {
                const res = await getPlatformArtistBindingDetail(this.artistId, this.platformTypeCode);
                if (res.data && res.data.state && res.data.result) {
                    const data = res.data.result;
                    this.platformArtistId = data.platformArtistId || '';
                    this.platformUrl = data.platformUrl || '';
                    this.linkedAt = data.addTime ? this.formatDate(data.addTime) : '';
                    this.syncedAt = data.syncTime ? this.formatDate(data.syncTime) : '';
                    this.bindStatus = data.status || 0;
                    this.isBound = this.bindStatus === 1;

                    if (data.platformInfo) {
                        try {
                            const parsedInfo = typeof data.platformInfo === 'string' ? JSON.parse(data.platformInfo) : data.platformInfo;
                            // 解析代表作：优先works数组，QQ音乐取对象的name字段
                            const repWorks = this.extractNames(parsedInfo.works || parsedInfo.representative || parsedInfo.representativeWorks || []);
                            // 解析乐队成员：优先members数组，QQ音乐取对象的name字段
                            const bandMembers = this.extractNames(parsedInfo.members || parsedInfo.bandMembers || []);
                            this.platformInfo = {
                                avatar: parsedInfo.coverUrl || parsedInfo.avatar || parsedInfo.picUrl || parsedInfo.img || '',
                                name: parsedInfo.name || parsedInfo.artistName || '',
                                fansCount: parsedInfo?.fans || parsedInfo?.fansCount || 0,
                                intro: parsedInfo.desc || parsedInfo.brief || parsedInfo.intro || parsedInfo.briefDesc || '',
                                representative: repWorks,
                                members: bandMembers
                            };
                            this.hasPlatformInfo = !!(this.platformInfo.avatar || this.platformInfo.name);
                        } catch (e) {
                            this.hasPlatformInfo = false;
                        }
                    } else {
                        this.hasPlatformInfo = false;
                    }
                } else {
                    this.isBound = false;
                    this.bindStatus = 0;
                    this.platformArtistId = '';
                    this.hasPlatformInfo = false;
                    this.linkedAt = '';
                    this.syncedAt = '';
                    this.platformUrl = '';
                }
            } catch (e) {
                this.isBound = false;
                this.bindStatus = 0;
                this.platformArtistId = '';
                this.hasPlatformInfo = false;
                this.linkedAt = '';
                this.syncedAt = '';
                this.platformUrl = '';
            } finally {
                this.loading = false;
                // 如果是drawer模式且需要默认进入绑定模式且未关联，直接进入绑定页面
                if (this.isDrawer && this.defaultBindMode && !this.isBound) {
                    this.$nextTick(() => {
                        this.goToBind();
                    });
                }
            }
        },
        initData() {
            this.fetchBindingDetail();
        },
        formatNumber(num) {
            if (num === undefined || num === null) return '0';
            return num.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',');
        },
        formatDate(date) {
            if (!date) return '';
            const d = new Date(date);
            const year = d.getFullYear();
            const month = String(d.getMonth() + 1).padStart(2, '0');
            const day = String(d.getDate()).padStart(2, '0');
            const hour = String(d.getHours()).padStart(2, '0');
            const minute = String(d.getMinutes()).padStart(2, '0');
            return `${year}-${month}-${day} ${hour}:${minute}`;
        },
        copyId() {
            const input = document.createElement('input');
            input.value = this.platformArtistId;
            document.body.appendChild(input);
            input.select();
            document.execCommand('copy');
            document.body.removeChild(input);
            this.$message.success('ID已复制');
        },
        getHomeUrl() {
            if (this.platformUrl) return this.platformUrl;
            if (!this.platformArtistId) return '';
            return this.platformType === 'netease'
                ? `https://music.163.com/#/artist?id=${this.platformArtistId}`
                : `https://y.qq.com/n/ryqq_v2/singer/${this.platformArtistId}`;
        },
        openHomePage() {
            const url = this.getHomeUrl();
            if (url) window.open(url);
        },
        getManualHomeUrl() {
            if (!this.manualId) return this.platformType === 'netease' ? 'https://music.163.com/#/artist?id=' : 'https://y.qq.com/n/ryqq_v2/singer/';
            return this.platformType === 'netease'
                ? `https://music.163.com/#/artist?id=${this.manualId}`
                : `https://y.qq.com/n/ryqq_v2/singer/${this.manualId}`;
        },
        openManualHomePage() {
            if (this.manualId) window.open(this.getManualHomeUrl());
        },
        openResultHome(item) {
            if (item.url) {
                window.open(item.url);
                return;
            }
            const url =
                this.platformType === 'netease' ? `https://music.163.com/#/artist?id=${item.id}` : `https://y.qq.com/n/ryqq_v2/singer/${item.id}`;
            window.open(url);
        },
        goBack() {
            if (this.isDrawer) {
                this.$emit('close');
            } else {
                this.$router.push({name: 'library-artist-info', params: {artistId: this.artistId}});
            }
        },
        async handleSync() {
            this.syncLoading = true;
            try {
                const res = await syncPlatformArtist({
                    artistId: this.artistId,
                    platformType: this.platformTypeCode
                });
                if (!res.data.state) {
                    this.$message.error(res.data.msg || '同步失败');
                    return;
                }
                this.$message.success('同步成功');
                await this.fetchBindingDetail();
            } catch (e) {
                this.$message.error('操作失败');
            } finally {
                this.syncLoading = false;
            }
        },
        async handleUnbind() {
            try {
                await this.$confirm('确定要解除关联吗？', '提示', {
                    confirmButtonText: '确定',
                    cancelButtonText: '取消'
                });
            } catch (e) {
                return;
            }

            try {
                const res = await unrelatePlatformArtist({
                    artistId: this.artistId,
                    platformType: this.platformTypeCode
                });
                if (!res.data.state) {
                    this.$message.error(res.data.msg || '解除关联失败');
                    return;
                }
                this.$message.success('已解除关联');
                await this.fetchBindingDetail();
                this.$emit('bind-changed');
            } catch (e) {
                this.$message.error('操作失败');
            }
        },
        goToBind() {
            this.relinkDialogVisible = false;
            this.pageMode = 'bind';
            this.searchKeyword = this.artistName || '';
            this.searchId = '';
            this.searchResults = [];
            this.selectedResult = -1;
            this.currentPage = 1;
            this.totalCount = 0;
            this.totalPage = 0;
            this.idSearchResult = null;
            this.idSearchError = '';
            this.manualMode = false;
            this.manualId = '';
            this.searchError = '';
            this.hasSearchedName = false;
            this.hasSearchedId = false;
            // 如果有艺人名称，自动搜索
            if (this.artistName) {
                this.$nextTick(() => {
                    this.handleSearch(1);
                });
            }
        },
        showRelinkConfirm() {
            this.relinkDialogVisible = true;
        },
        continueRelink() {
            this.goToBind();
        },
        cancelBind() {
            if (this.isDrawer && !this.isBound) {
                this.$emit('close');
            } else {
                this.pageMode = 'detail';
            }
        },
        switchTab(tab) {
            this.searchTab = tab;
            if (tab === 'create') {
                this.searchKeyword = '';
                this.searchId = '';
                this.searchResults = [];
                this.selectedResult = -1;
                this.idSearchResult = null;
                this.idSearchError = '';
                this.manualMode = false;
                this.manualId = '';
                this.searchError = '';
                this.hasSearchedName = false;
                this.hasSearchedId = false;
                return;
            }
            this.searchKeyword = '';
            this.searchId = '';
            this.searchResults = [];
            this.selectedResult = -1;
            this.currentPage = 1;
            this.totalCount = 0;
            this.totalPage = 0;
            this.idSearchResult = null;
            this.idSearchError = '';
            this.manualMode = false;
            this.manualId = '';
            this.searchError = '';
            if (tab === 'name') {
                this.hasSearchedId = false;
            } else {
                this.hasSearchedName = false;
            }
        },
        switchToManual() {
            this.manualMode = true;
            this.searchError = '';
            this.idSearchError = '';
            this.idSearchResult = null;
        },
        async handleSearch(page = 1) {
            if (!this.searchKeyword.trim()) {
                this.$message.error('请输入艺人名称');
                return;
            }
            const targetPage = typeof page === 'number' ? page : 1;
            this.searchLoading = true;
            this.searchError = '';
            this.searchResults = [];
            this.selectedResult = -1;
            this.manualMode = false;
            this.hasSearchedName = true;
            this.currentPage = targetPage;
            try {
                const res = await searchPlatformArtist({
                    keyword: this.searchKeyword.trim(),
                    platformType: this.platformTypeCode,
                    pageNo: targetPage,
                    pageSize: this.pageSize
                });
                if (!res.data.state) {
                    this.searchError = res.data.msg || '暂时无法获取平台艺人信息，请稍后重试，或手动输入艺人 ID 完成关联。';
                    return;
                }
                const result = res.data.result;
                const list = (result && result.result) || (result && result.list) || [];
                if (list.length === 0) {
                    this.searchError = '暂时无法获取平台艺人信息，请稍后重试，或手动输入艺人 ID 完成关联。';
                    return;
                }
                this.totalCount = (result && result.totalCount) || list.length;
                this.totalPage = (result && result.totalPage) || Math.ceil(this.totalCount / this.pageSize);
                this.searchResults = list.map(item => ({
                    id: item.artistId || item.id || item.platformArtistId || '',
                    name: item.name || item.artistName || '',
                    avatar: item.coverUrl || item.avatar || item.picUrl || item.img || '',
                    representative: this.extractNames(item.works || item.representative || item.representativeWorks || []),
                    url: item.url || ''
                }));
                if (this.searchResults.length > 0) {
                    this.selectedResult = 0;
                }
            } catch (e) {
                this.searchError = '暂时无法获取平台艺人信息，请稍后重试，或手动输入艺人 ID 完成关联。';
            } finally {
                this.searchLoading = false;
            }
        },
        handlePageChange(page) {
            this.handleSearch(page);
        },
        handleSizeChange(size) {
            this.pageSize = size;
            this.currentPage = 1;
            this.handleSearch(1);
        },
        async handleSearchById() {
            if (!this.searchId.trim()) {
                this.$message.error('请输入艺人ID');
                return;
            }
            this.searchLoading = true;
            this.idSearchResult = null;
            this.idSearchError = '';
            this.manualMode = false;
            this.hasSearchedId = true;
            try {
                const res = await searchPlatformArtist({
                    platformArtistId: this.searchId.trim(),
                    platformType: this.platformTypeCode,
                    pageNo: 1,
                    pageSize: 1
                });
                if (!res.data.state) {
                    this.idSearchError = res.data.msg || '暂时无法获取平台艺人信息，请稍后重试，或手动输入艺人 ID 完成关联。';
                    return;
                }
                const result = res.data.result;
                const list = (result && result.result) || (result && result.list) || [];
                if (list.length === 0) {
                    this.idSearchError = '暂时无法获取平台艺人信息，请稍后重试，或手动输入艺人 ID 完成关联。';
                    return;
                }
                const item = list[0];
                this.idSearchResult = {
                    id: item.artistId || item.id || item.platformArtistId || this.searchId,
                    name: item.name || item.artistName || '',
                    avatar: item.coverUrl || item.avatar || item.picUrl || item.img || '',
                    fansCount: item.fansCount || item.fans || 0,
                    intro: item.desc || item.intro || item.brief || item.briefDesc || '',
                    representative: this.extractNames(item.works || item.representative || item.representativeWorks || []),
                    members: this.extractNames(item.members || item.bandMembers || []),
                    url: item.url || ''
                };
            } catch (e) {
                this.idSearchError = '暂时无法获取平台艺人信息，请稍后重试，或手动输入艺人 ID 完成关联。';
            } finally {
                this.searchLoading = false;
            }
        },
        getNewId() {
            if (this.searchTab === 'name' && this.selectedResult >= 0) {
                return this.searchResults[this.selectedResult].id;
            }
            if (this.searchTab === 'id' && this.idSearchResult) {
                return this.idSearchResult.id;
            }
            if (this.manualMode) {
                return this.manualId;
            }
            return '';
        },
        confirmBind() {
            if (this.manualMode) {
                if (!this.manualId.trim()) {
                    this.$message.error('请输入艺人ID');
                    return;
                }
                // 未关联状态弹确认关联框，已关联弹手动确认框
                if (this.isBound) {
                    this.manualDialogVisible = true;
                } else {
                    this.linkConfirmDialogVisible = true;
                }
                return;
            }
            if (this.searchTab === 'name') {
                if (this.selectedResult < 0) {
                    this.$message.error('请选择要关联的艺人');
                    return;
                }
            } else {
                if (!this.idSearchResult) {
                    this.$message.error('请先查询艺人信息');
                    return;
                }
            }
            // 未关联状态弹确认关联框，已关联弹替换确认框
            if (this.isBound) {
                this.replaceDialogVisible = true;
            } else {
                this.linkConfirmDialogVisible = true;
            }
        },
        confirmLink() {
            this.linkConfirmDialogVisible = false;
            if (this.manualMode) {
                this.doManualBind();
            } else {
                this.doReplace();
            }
        },
        async doReplace() {
            this.replaceDialogVisible = false;
            this.submitLoading = true;
            const newId = this.getNewId();
            const platformTypeVal = this.platformType === 'netease' ? 1 : 2;
            try {
                const res = await relatePlatformArtist({
                    opUserId: this.$store.state.userInfo?.id,
                    artistId: parseInt(this.artistId),
                    platformType: platformTypeVal,
                    platformArtistId: newId,
                    idSource: 1
                });
                if (!res.data.state) {
                    this.$message.error(res.data.msg);
                    return;
                }
                this.$message.success('关联成功');
                this.pageMode = 'detail';
                // 重新获取最新绑定详情
                await this.fetchBindingDetail();
            } catch (e) {
                this.$message.error('操作失败');
            } finally {
                this.submitLoading = false;
            }
        },
        async doManualBind() {
            this.manualDialogVisible = false;
            this.submitLoading = true;
            const platformTypeVal = this.platformType === 'netease' ? 1 : 2;
            try {
                const res = await relatePlatformArtist({
                    opUserId: this.$store.state.userInfo?.id,
                    artistId: parseInt(this.artistId),
                    platformType: platformTypeVal,
                    platformArtistId: this.manualId,
                    idSource: 2
                });
                if (!res.data.state) {
                    this.$message.error(res.data.msg);
                    return;
                }
                this.$message.success('关联成功');
                this.pageMode = 'detail';
                // 重新获取最新绑定详情
                await this.fetchBindingDetail();
            } catch (e) {
                this.$message.error('操作失败');
            } finally {
                this.submitLoading = false;
            }
        },
        /**
         * 确认创建平台主页
         */
        async confirmCreate() {
            this.submitLoading = true;
            try {
                const res = await markCreatePlatformArtist({
                    ids: this.artistId,
                    platformType: this.platformTypeCode
                });
                if (!res.data.state) {
                    this.$message.error(res.data.msg || '创建失败');
                    return;
                }
                this.$message.success('新建主页成功');
                this.pageMode = 'detail';
                this.defaultBindMode = false;
                await this.fetchBindingDetail();
                this.$emit('bind-changed');
            } catch (e) {
                this.$message.error('操作失败');
            } finally {
                this.submitLoading = false;
            }
        }
    }
};
</script>

<style lang="scss" scoped>
.platform-detail-page {
    min-height: calc(100vh - 120px);
    background: #f5f7fa;
    margin: -24px -24px -24px;
    padding: 24px;
}

.sync-error-bar {
    background: #fef0f0;
    border: 1px solid #fde2e2;
    color: #f56c6c;
    padding: 10px 16px;
    border-radius: 4px;
    margin-bottom: 16px;
    text-align: center;
}

.info-card {
    ::v-deep {
        .el-card {
            border: none;
            border-radius: 4px;
            box-shadow: none;
            background: #fff;
            border: 1px solid #ebeef5;
        }

        .el-card__header {
            padding: 20px;
            border-bottom: 1px solid #ebeef5;
        }

        .el-card__body {
            padding: 20px;
        }
    }

    .card-header {
        display: flex;
        justify-content: space-between;
        align-items: center;
        margin-bottom: 20px;
    }
}

.platform-title-row {
    display: flex;
    align-items: center;
    gap: 12px;
}

.platform-logo {
    width: 40px;
    height: 40px;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
    line-height: 0;
    vertical-align: middle;

    &.qq-logo {
        background: #ffffff;
        border-radius: 9px;
        box-shadow: 0 0 0 1px rgba(0, 0, 0, 0.04);

        ::v-deep {
            .svg-icon {
                width: 36px !important;
                height: 36px !important;
                font-size: 36px !important;
            }

            svg {
                width: 36px !important;
                height: 36px !important;
            }
        }
    }

    ::v-deep {
        .svg-icon {
            width: 40px !important;
            height: 40px !important;
            font-size: 40px !important;
            display: block;
            line-height: 1;
        }

        svg {
            display: block;
            width: 40px !important;
            height: 40px !important;
        }
    }
}

.platform-name-text {
    font-size: 18px;
    font-weight: 600;
    color: #303133;
}

.badge {
    font-size: 14px;
    font-weight: bold;
    padding: 3px 8px;
    border-radius: 10px;

    &.badge-bound {
        background: #f0f9eb;
        color: #67c23a;
    }

    &.badge-unbound {
        background: #f4f4f5;
        color: #909399;
    }

    &.badge-special {
        background: #fdf6ec;
        color: #e6a23c;
    }
}

.info-rows {
    font-size: 14px;
}

.info-row-two-col {
    display: flex;
    justify-content: space-between;
    margin-bottom: 16px;

    &:last-child {
        margin-bottom: 0;
    }
}

.info-item {
    flex: 1;
    display: flex;
    align-items: center;
}

.info-label {
    color: #909399;
    margin-right: 12px;
    flex-shrink: 0;
}

.info-value {
    color: #303133;

    &.link {
        color: #409eff;
        cursor: pointer;
    }
}

.copy-btn {
    color: #409eff;
    margin-left: 12px;
    padding: 0;
    font-size: 14px;
}

.card-title {
    font-size: 20px;
    font-weight: 600;
    color: #303133;
    padding: 0;
}

.platform-info-section {
    margin-top: 20px;
}

.section-title {
    font-size: 20px;
    font-weight: 600;
    color: #303133;
    margin-bottom: 20px;
}

.profile-content {
    display: flex;
    gap: 20px;
}

.avatar-wrapper {
    width: 100px;
    height: 100px;
    flex-shrink: 0;
}

.profile-avatar {
    width: 100px !important;
    height: 100px !important;
    border-radius: 12px !important;
    cursor: pointer;
}

.avatar-placeholder.profile-placeholder {
    width: 100px;
    height: 100px;
    border-radius: 12px;
    flex-direction: column;
    font-size: 14px;

    i {
        font-size: 32px;
        margin-bottom: 4px;
    }

    .placeholder-text {
        font-size: 12px;
        color: #c0c4cc;
        line-height: 1;
    }
}

.avatar {
    width: 120px;
    height: 120px;
    border-radius: 8px;
}

.avatar-placeholder {
    width: 120px;
    height: 120px;
    border-radius: 8px;
    background: #f0f2f5;
    display: flex;
    align-items: center;
    justify-content: center;
    color: #909399;
    font-size: 36px;
}

.profile-info {
    flex: 1;
}

.artist-name {
    font-size: 24px;
    font-weight: bold;
    line-height: 1.2;
    color: #303133;
}

.fans-count {
    margin-top: 8px;

    strong {
        font-size: 24px;
        font-weight: bold;
        color: #303133;
    }

    span {
        margin-left: 6px;
        font-size: 14px;
        color: #909399;
    }
}

.artist-desc {
    margin-top: 12px;
    font-size: 16px;
    color: #606266;
    line-height: 1.6;
}

.tags-line {
    margin-top: 16px;
    font-size: 16px;
    color: #909399;
    display: flex;
    align-items: center;
    flex-wrap: wrap;

    .tags-label {
        flex-shrink: 0;
    }

    .info-tag {
        display: inline-flex;
        align-items: center;
        padding: 4px 12px;
        // background: #f5f7fa;
        border-radius: 16px;
        font-size: 14px;
        color: #606266;
    }
}

.unsync-list {
    font-size: 14px;
}

.unsync-row {
    padding: 14px 0;
    border-bottom: none;
}

.unsync-label {
    color: #303133;
    display: inline-block;
    width: 120px;
}

.unsync-value {
    color: #c0c4cc;
}

.empty-hint {
    text-align: center;
    padding: 40px 0;
    color: #909399;
}

.actions-bar {
    display: flex;
    justify-content: flex-end;
    gap: 14px;
    padding: 24px 0 0;

    .el-button {
        min-width: 110px;
        border-radius: 6px;
        font-size: 15px;

        &--default {
            border-color: #dcdfe6;
            color: #606266;
            background: #fff;
        }

        &--danger.is-plain {
            color: #f56c6c;
            background: #fff;
            border-color: #fbc4c4;

            &:hover,
            &:focus {
                background: #fef0f0;
                color: #f56c6c;
                border-color: #fbc4c4;
            }
        }

        &--primary {
            background: #409eff;
            border-color: #409eff;

            &:hover,
            &:focus {
                background: #66b1ff;
                border-color: #66b1ff;
            }
        }
    }
}

.sync-btn.is-disabled-btn.el-button.is-disabled,
.sync-btn.is-disabled-btn.el-button.is-disabled:hover,
.sync-btn.is-disabled-btn.el-button.is-disabled:focus {
    background-color: #f5f7fa !important;
    border-color: #e4e7ed !important;
    color: #c0c4cc !important;
    cursor: not-allowed !important;
    opacity: 1 !important;
}

.m-top_8 {
    margin-top: 8px;
}

.m-top_12 {
    margin-top: 12px;
}

.m-top_16 {
    margin-top: 16px;
}

.m-top_24 {
    margin-top: 24px;
}

.bind-card {
    ::v-deep {
        .el-card {
            border: none;
            border-radius: 4px;
            box-shadow: none;
            background: #fff;
            border: 1px solid #ebeef5;
        }

        .el-card__body {
            padding: 24px;
        }
    }
}

.tab-switch {
    display: flex;
    margin-bottom: 24px;
}

.tab-btn {
    padding: 12px 28px;
    font-size: 16px;
    cursor: pointer;
    border: 1px solid #dcdfe6;
    background: #fff;
    color: #303133;
    font-weight: 500;

    &:first-child {
        border-top-left-radius: 4px;
        border-bottom-left-radius: 4px;
        border-right: none;
    }

    &:last-child {
        border-top-right-radius: 4px;
        border-bottom-right-radius: 4px;
    }

    &.active {
        background: #409eff;
        color: #fff;
        border-color: #409eff;
    }
}

.search-row {
    display: flex;
    gap: 12px;
}

.search-input {
    flex: 1;

    ::v-deep {
        .el-input__inner {
            height: 48px;
            line-height: 48px;
            font-size: 15px;
            border-radius: 4px;
        }
    }
}

.search-btn {
    width: 80px;
    height: 48px;
    font-size: 15px;
    border-radius: 4px;
}

.error-warn {
    padding: 14px 16px;
    background: #fdf6ec;
    border: 1px solid #faecd8;
    border-radius: 4px;
    margin-top: 16px;
}

.error-text {
    color: #e6a23c;
    font-size: 14px;
    margin-bottom: 12px;

    i {
        margin-right: 6px;
    }
}

.error-actions {
    display: flex;
    gap: 10px;
}

.manual-tip {
    font-size: 14px;

    .tip-label {
        color: #909399;
    }

    .tip-link {
        color: #409eff;
        cursor: pointer;
    }
}

.results-list {
    margin-top: 20px;
}

.result-item {
    display: flex;
    justify-content: space-between;
    align-items: center;
    border: 1px solid #e4e7ed;
    border-radius: 6px;
    padding: 12px 16px;
    margin-bottom: 12px;
    cursor: pointer;
    transition: all 0.2s;

    &:hover {
        border-color: #b3d8ff;
    }

    &.active {
        border-color: #409eff;
        background: #ecf5ff;
    }
}

.result-left {
    display: flex;
    align-items: center;
    gap: 12px;
}

.custom-radio {
    width: 16px;
    height: 16px;
    border: 1px solid #dcdfe6;
    border-radius: 50%;
    flex-shrink: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    transition: all 0.2s;
    background: #fff;
    box-sizing: border-box;

    &.checked {
        border-color: #409eff;
        background: #409eff;
    }

    .radio-dot {
        width: 4px;
        height: 4px;
        background: #fff;
        border-radius: 50%;
    }
}

.result-avatar {
    width: 50px;
    height: 50px;
    border-radius: 6px;
    overflow: hidden;
    background: #f0f2f5;
    flex-shrink: 0;
}

.avatar-placeholder-small {
    width: 50px;
    height: 50px;
    background: #f0f2f5;
    display: flex;
    align-items: center;
    justify-content: center;
    color: #909399;
    font-size: 28px;
    border-radius: 8px;
}

.result-info {
    .result-name {
        font-size: 16px;
        font-weight: 600;
        color: #303133;
        margin-bottom: 4px;
    }

    .result-sub {
        font-size: 13px;
        color: #909399;
        line-height: 1.6;
    }
}

.open-home-btn {
    color: #409eff !important;
    font-size: 14px;
    padding: 0;
    font-weight: 500;
}

.no-data {
    text-align: center;
    padding: 48px 0;
    color: #909399;
    font-size: 14px;
}

.pagination-wrapper {
    margin-top: 20px;
    display: flex;
    justify-content: flex-end;
}

/* ID直接关联结果样式 */
.id-profile-content {
    display: flex;
    gap: 20px;
}

.id-avatar-wrapper {
    width: 100px;
    height: 100px;
    flex-shrink: 0;
}

.id-avatar {
    width: 100px;
    height: 100px;
    border-radius: 12px;
}

.id-avatar-placeholder {
    width: 100px;
    height: 100px;
    border-radius: 12px;
    background: #f0f2f5;
    display: flex;
    align-items: center;
    justify-content: center;
    color: #909399;
    flex-direction: column;

    i {
        font-size: 32px;
        margin-bottom: 4px;
    }

    .placeholder-text {
        font-size: 12px;
        color: #c0c4cc;
        line-height: 1;
    }
}

.id-profile-info {
    flex: 1;
}

.id-artist-name {
    font-size: 28px;
    font-weight: bold;
    line-height: 1.2;
    color: #303133;
}

.id-fans-count {
    margin-top: 8px;

    strong {
        font-size: 24px;
        font-weight: bold;
        color: #303133;
    }

    span {
        margin-left: 6px;
        font-size: 14px;
        color: #909399;
    }
}

.id-artist-desc {
    margin-top: 12px;
    font-size: 16px;
    color: #606266;
    line-height: 1.6;
}

.id-tags-line {
    margin-top: 16px;
    font-size: 18px;
    color: #909399;
    display: flex;
    align-items: center;
    flex-wrap: wrap;

    .id-tags-label {
        flex-shrink: 0;
    }

    .id-tag-item {
        display: inline-flex;
        align-items: center;
        padding: 4px 12px;
        // background: #f5f7fa;
        border-radius: 16px;
        font-size: 14px;
        color: #606266;
    }
}

.dialog-content {
    font-size: 14px;
    line-height: 1.8;

    strong {
        color: #409eff;
    }
}
.relink-dialog {
    top: 30%;
}

.create-main-page {
    padding: 60px 40px;
    text-align: center;

    .create-title {
        font-size: 20px;
        font-weight: 600;
        color: #303133;
        margin-bottom: 24px;
    }

    .create-tip {
        font-size: 14px;
        color: #909399;
        line-height: 1.8;
    }
}
</style>
